// ==================== INPUT COMPONENT ====================

import { forwardRef, useId, useState } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../utils/helpers';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      fullWidth = true,
      type = 'text',
      showPasswordToggle = false,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const [showPassword, setShowPassword] = useState(false);

    // Determine the actual input type
    const isPasswordType = type === 'password';
    const actualType = isPasswordType && showPassword ? 'text' : type;

    // Show toggle only for password fields with showPasswordToggle enabled
    const shouldShowToggle = isPasswordType && showPasswordToggle;

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-(--color-soft-gray)"
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-muted-gray)">
              {leftIcon}
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            type={actualType}
            className={cn(
              `w-full px-4 py-2.5 text-sm text-white
              bg-(--color-navy-light) 
              border border-(--color-border-gray)
              rounded-md
              placeholder:text-(--color-muted-gray)
              transition-all duration-200
              focus:border-(--color-signal-orange)
              focus:ring-2 focus:ring-(--color-orange-glow)
              disabled:opacity-50 disabled:cursor-not-allowed`,
              leftIcon ? 'pl-10' : '',
              (rightIcon || shouldShowToggle) ? 'pr-10' : '',
              error ? 'border-(--color-error) focus:border-(--color-error) focus:ring-(--color-error-light)' : '',
              className
            )}
            {...props}
          />
          
          {shouldShowToggle ? (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-(--color-muted-gray) hover:text-(--color-soft-gray) transition-colors focus:outline-none"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          ) : rightIcon ? (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-(--color-muted-gray)">
              {rightIcon}
            </div>
          ) : null}
        </div>
        
        {error && (
          <p className="text-xs text-(--color-error)">{error}</p>
        )}
        
        {hint && !error && (
          <p className="text-xs text-(--color-muted-gray)">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };

